<script>
import every from 'lodash/every'
import values from 'lodash/values'
import replace from 'lodash/replace'
import {
  memberListComputed,
  memberListMethods,
} from '@state/helpers/member-list'
import SearchPayload from '@utils/searchPayload'
import { ObjectHelper, csvExport, addParamsToLocation } from '@utils/helpers'
import cloneDeep from 'lodash/cloneDeep'
import split from 'lodash/split'
import CsvExportPopup from '@components/molecules/csv-export-popup.vue'
import { endOfDay, startOfDay } from 'date-fns'
import { CSV_FILE_NAME } from '@utils/csvFilename'
import { DATE_TIME_FORMAT } from '@/src/utils/constant'
import formatDate from '@utils/format-date'
import tableFields from './tableFields'
import fieldsBoxCard from './fieldsBoxCard'

export default {
  components: { CsvExportPopup },
  page: {
    title: 'Member List',
    meta: [{ name: 'description', content: 'The Member List page.' }],
  },
  data() {
    return {
      items: {},
      searchBody: new SearchPayload.Builder()
        .withSearchName()
        .withSearchEmail()
        .withSearchAgent(),
      fields: tableFields,
      filtered: false,
      openConfirm: false,
      membersSelected: {},
      fieldsBoxCard: fieldsBoxCard,
      componentKey: 0,
      csvKey: 0,
    }
  },
  computed: {
    ...memberListComputed,
    memberListData() {
      const listMember = cloneDeep(this.getMemberList?.list)?.map((item) => {
        item.gender = item.gender === 0 ? '女性' : '男性'
        item.mobile_os = replace(item.mobile_os, 'iOS', '')
        return item
      })
      return listMember
    },
    paginationComputed() {
      let paginations = {}
      if (this.getMemberList) {
        paginations = ObjectHelper.removePropertyByKey('list')(
          this.getMemberList
        )
      }
      return paginations
    },
    unselectedAll() {
      return every(values(this.membersSelected), function(v) {
        return !v
      })
    },
    confirmComputed() {
      if (this.unselectedAll) {
        return 'unselected'
      }
      return 'quitagent'
    },
  },
  watch: {
    searchBody() {
      addParamsToLocation(this.$route, this.searchBody)
      this.componentKey++
    },
  },
  created() {
    const filter = this.$route.query
    filter.page_no = parseInt(filter.page_no, 10)
    filter.lines = parseInt(filter.lines, 10)
    for (const key in filter) {
      if (key !== 'page_no' && key !== 'lines') {
        if (filter[key] !== '' && filter[key] !== undefined) {
          this.searchBody = filter
          this.filtered = true
        }
      }
    }
    if (filter.page_no > 1) {
      this.searchBody = filter
    }
    this.handleSearchMember()
  },
  methods: {
    ...memberListMethods,
    showModal() {
      this.openConfirm = !this.openConfirm
    },
    handlePagination(body) {
      this.searchBody = { ...this.searchBody, ...body }
      this.handleSearchMember()
    },
    handleSorter(key) {
      this.searchBody = { ...this.searchBody, order_by: key }
      this.handleSearchMember()
    },
    handleFilter(params, reset = false) {
      this.searchBody = { ...this.searchBody, ...params }
      this.handleSearchMember()
      this.filtered = !reset
    },
    handleSearchMember() {
      this.fetchingMemberList(this.searchBody)
    },
    goToAddNewPage() {
      this.$router.push({ name: 'memberRegistration' })
    },
    handleSelected(o, value) {
      const temp = { ...this.membersSelected }
      temp[o.agentId + '-' + o.memberId] = value
      this.membersSelected = { ...temp }
    },
    viewDetail(item) {
      this.$router.push({
        path: `/panel/member-list/detail/${item.member_id}/${item.agent_id}`,
      })
    },
    submit() {
      const temp = { ...this.membersSelected }
      const flag = Object.keys(temp)?.map((key) => {
        if (temp[key]) {
          const item = split(key, '-', 2)
          return {
            agent_id: item[0],
            member_id: item[1],
          }
        }
      })
      Promise.all(
        flag?.map((obj) => {
          return this.deleteMemberById(obj)
        })
      ).then((rs) => {
        this.openConfirm = !this.openConfirm
        this.fetchingMemberList(this.searchBody)
        this.membersSelected = {}
      })
    },
    async exportCsv(date) {
      date.date_end = formatDate(
        endOfDay(new Date(date.date_end)),
        DATE_TIME_FORMAT
      )
      date.date_start = formatDate(
        startOfDay(new Date(date.date_start)),
        DATE_TIME_FORMAT
      )
      const response = await this.exportMemberCsv({
        ...this.searchBody,
        ...date,
      })

      csvExport(response, CSV_FILE_NAME.memberList)
      this.csvKey++
    },
  },
}
</script>

<template>
  <div>
    <BaseFilterBoxCard
      :key="componentKey"
      :filter-value="searchBody"
      :fields="fieldsBoxCard"
      @filter="(p, r) => handleFilter(p, r)"
    />
    <BasePagination
      :paginations="paginationComputed"
      :searching="filtered"
      @activePagination="handlePagination"
    >
      <template v-slot:secondary>
        <CButton
          color="dark"
          variant="outline"
          class="py-0"
          @click="goToAddNewPage"
          >新規登録</CButton
        >
        <CButton
          color="dark"
          variant="outline"
          class="py-0 ml-1"
          @click="showModal"
          >{{ $t('confirm.quitagent.btn') }}</CButton
        >
        <CsvExportPopup :key="csvKey" @export="exportCsv" />
      </template>
    </BasePagination>
    <BaseTable
      striped
      border
      hover
      :table-fields="fields"
      :data-tables="memberListData"
      @sorterBy="handleSorter"
      @row-clicked="(item) => viewDetail(item)"
    >
      <template v-slot:checkbox="{ item }">
        <td class="py-2" @click.stop="() => {}">
          <CInputCheckbox
            v-if="item.agent_id !== 1 || item.master_member_id === null"
            :checked="membersSelected[item.agent_id + '-' + item.member_id]"
            color="primary"
            @update:checked="
              (v) =>
                handleSelected(
                  { agentId: item.agent_id, memberId: item.member_id },
                  v
                )
            "
          />
        </td>
      </template>
      <template v-slot:account_name="{ item }">
        <td>
          {{ item.account_name }}
        </td>
      </template>
    </BaseTable>
    <BaseModalConfirm
      backdrop-close
      :show.sync="openConfirm"
      :type="confirmComputed"
      :hide-action-button="unselectedAll"
      @closeModal="openConfirm = !openConfirm"
      @submit="submit"
    />
  </div>
</template>
